/**
 * Token Storage Helper
 * Handles token storage with fallbacks for iOS Safari issues
 */

const TOKEN_KEY = 'authToken';

/**
 * Test if localStorage is available and working
 */
const isLocalStorageAvailable = (): boolean => {
  try {
    const testKey = '__localStorage_test__';
    localStorage.setItem(testKey, 'test');
    localStorage.removeItem(testKey);
    return true;
  } catch (e) {
    console.warn('[TokenStorage] localStorage not available:', e);
    return false;
  }
};

/**
 * Fallback storage using sessionStorage
 */
const sessionStorageFallback = {
  setItem: (key: string, value: string) => {
    try {
      sessionStorage.setItem(key, value);
      console.log('[TokenStorage] Stored in sessionStorage');
    } catch (e) {
      console.error('[TokenStorage] sessionStorage failed:', e);
    }
  },
  getItem: (key: string): string | null => {
    try {
      return sessionStorage.getItem(key);
    } catch (e) {
      console.error('[TokenStorage] sessionStorage read failed:', e);
      return null;
    }
  },
  removeItem: (key: string) => {
    try {
      sessionStorage.removeItem(key);
    } catch (e) {
      console.error('[TokenStorage] sessionStorage remove failed:', e);
    }
  },
};

/**
 * Memory fallback (last resort)
 */
let memoryStorage: { [key: string]: string } = {};

const memoryFallback = {
  setItem: (key: string, value: string) => {
    memoryStorage[key] = value;
    console.log('[TokenStorage] Stored in memory');
  },
  getItem: (key: string): string | null => {
    return memoryStorage[key] || null;
  },
  removeItem: (key: string) => {
    delete memoryStorage[key];
  },
};

/**
 * Get the appropriate storage mechanism
 */
const getStorage = () => {
  if (isLocalStorageAvailable()) {
    return localStorage;
  }
  
  // Try sessionStorage as fallback
  try {
    const testKey = '__sessionStorage_test__';
    sessionStorage.setItem(testKey, 'test');
    sessionStorage.removeItem(testKey);
    console.warn('[TokenStorage] Using sessionStorage fallback');
    return sessionStorageFallback;
  } catch (e) {
    console.warn('[TokenStorage] Using memory fallback');
    return memoryFallback;
  }
};

/**
 * Save auth token
 */
export const saveToken = (token: string): boolean => {
  try {
    const storage = getStorage();
    storage.setItem(TOKEN_KEY, token);
    
    // Verify it was saved
    const saved = storage.getItem(TOKEN_KEY);
    const success = saved === token;
    console.log('[TokenStorage] Save token:', success ? 'SUCCESS' : 'FAILED');
    return success;
  } catch (error) {
    console.error('[TokenStorage] Error saving token:', error);
    return false;
  }
};

/**
 * Get auth token
 */
export const getToken = (): string | null => {
  try {
    const storage = getStorage();
    const token = storage.getItem(TOKEN_KEY);
    return token;
  } catch (error) {
    console.error('[TokenStorage] Error getting token:', error);
    return null;
  }
};

/**
 * Remove auth token
 */
export const removeToken = (): void => {
  try {
    const storage = getStorage();
    storage.removeItem(TOKEN_KEY);
    
    // Also clear from all possible locations
    try { localStorage.removeItem(TOKEN_KEY); } catch (e) { /* ignore */ }
    try { sessionStorage.removeItem(TOKEN_KEY); } catch (e) { /* ignore */ }
    memoryStorage = {};
    
    console.log('[TokenStorage] Token removed');
  } catch (error) {
    console.error('[TokenStorage] Error removing token:', error);
  }
};
